// src/pages/ClaimDossier/useTaxConfig.ts
// Mục 7.1 (README.md) — ngưỡng + tỷ lệ khấu trừ Thuế TNCN lấy từ system-service
// (SystemConfiguration.TaxThreshold/TaxRate), KHÔNG còn fix cứng 10.000.000đ / 10%.
// Hook này chỉ phục vụ hiển thị đúng chữ mô tả ở màn Thẩm định Hồ sơ — số tiền thuế thực tế vẫn do
// backend tính và trả về qua field withholdingTax, hook KHÔNG tự tính lại.

import { useEffect, useState } from 'react';
import { getTaxConfig } from 'helpers/fakebackend_helper';

export interface TaxConfig {
  taxThreshold: number; // VND
  taxRate: number; // % (0-100)
  personalInfoThreshold: number; // VND (mục 7 §7) — giá trị giải thưởng gốc VƯỢT ngưỡng này mới bắt buộc thu thập thông tin cá nhân
  // Bổ sung — % hoa hồng TRẢ THƯỞNG đại lý được nhận (SystemConfiguration.DefaultPrizePayoutCommissionRate, 0-100).
  // Dùng ở cột "% HH" / "Tiền HH" của danh sách hồ sơ trả thưởng trực tiếp.
  prizePayoutCommissionRate: number;
  // Thời hạn trả thưởng hợp lệ (ngày) tính từ ngày mở thưởng in trên vé (SystemConfiguration.PrizeClaimValidDays).
  prizeClaimValidDays: number;
}

// Fallback khi chưa tải xong / lỗi API — trùng giá trị mặc định lịch sử để không nhảy số gây khó hiểu.
export const DEFAULT_TAX_CONFIG: TaxConfig = {
  taxThreshold: 10_000_000,
  taxRate: 10,
  personalInfoThreshold: 1_000_000,
  prizePayoutCommissionRate: 0,
  prizeClaimValidDays: 30,
};

export const useTaxConfig = (): TaxConfig => {
  const [config, setConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  useEffect(() => {
    let mounted = true;
    getTaxConfig()
      .then((data: any) => {
        // axios interceptor đã unwrap sẵn response.data → data là { taxThreshold, taxRate }
        if (
          mounted &&
          data &&
          typeof data.taxThreshold === 'number' &&
          typeof data.taxRate === 'number'
        ) {
          setConfig({
            taxThreshold: data.taxThreshold,
            taxRate: data.taxRate,
            personalInfoThreshold:
              typeof data.prizeClaimPersonalInfoThreshold === 'number' &&
              data.prizeClaimPersonalInfoThreshold > 0
                ? data.prizeClaimPersonalInfoThreshold
                : DEFAULT_TAX_CONFIG.personalInfoThreshold,
            prizePayoutCommissionRate:
              typeof data.defaultPrizePayoutCommissionRate === 'number'
                ? data.defaultPrizePayoutCommissionRate
                : DEFAULT_TAX_CONFIG.prizePayoutCommissionRate,
            prizeClaimValidDays:
              typeof data.prizeClaimValidDays === 'number' && data.prizeClaimValidDays > 0
                ? data.prizeClaimValidDays
                : DEFAULT_TAX_CONFIG.prizeClaimValidDays,
          });
        }
      })
      .catch(() => undefined); // giữ mặc định, không chặn UI
    return () => {
      mounted = false;
    };
  }, []);

  return config;
};

// "10.000.000 ₫" — định dạng ngưỡng theo locale VN
export const formatTaxThreshold = (cfg: TaxConfig): string =>
  `${Number(cfg.taxThreshold).toLocaleString('vi-VN')} ₫`;

// "10%" — tỷ lệ khấu trừ
export const formatTaxRate = (cfg: TaxConfig): string => `${cfg.taxRate}%`;

// Bổ sung — HH trả thưởng đại lý nhận = PrizeAmount (giá trị giải GỐC) × % cấu hình hệ thống.
// Cùng công thức với ticket-service AgentDebtLedgerRebuildService (mục 4 — PrizePayoutCommission).
export const computePrizePayoutCommission = (prizeAmount: number, cfg: TaxConfig): number => {
  const amount = Number(prizeAmount) || 0;
  return Math.round(amount * (Number(cfg.prizePayoutCommissionRate) / 100));
};

// Mục 8 (README.md §7) — cùng công thức với payout-service PrizeClaimTaxCalculator:
// Thuế = round( taxRate% × MAX(0, PrizeAmount − TaxThreshold) ); Thực lĩnh = PrizeAmount − Thuế.
// Dùng để hiển thị LẠI đúng số theo cấu hình HIỆN TẠI ở màn Thẩm định cho hồ sơ chưa ký duyệt —
// khi ký duyệt (ApprovePrizeClaimDossierCommand) backend ghi đè lại đúng số này vào DB.
export const computeWithholdingTax = (
  prizeAmount: number,
  cfg: TaxConfig,
): { tax: number; net: number } => {
  const amount = Number(prizeAmount) || 0;
  const taxable = Math.max(0, amount - Number(cfg.taxThreshold));
  const tax = Math.round(taxable * (Number(cfg.taxRate) / 100));
  return { tax, net: amount - tax };
};

// Hồ sơ CHƯA ký duyệt (PendingApproval) — hiển thị Thuế/Thực lĩnh tính lại theo cấu hình hiện tại,
// vì khi ký duyệt ApprovePrizeClaimDossierCommand sẽ ghi đè đúng số này vào DB. Hồ sơ AgentPaid
// KHÔNG tính lại (đại lý đã chi trả trực tiếp, công nợ đã chốt lúc tạo).
export const isTaxRecomputable = (status?: string): boolean => status === 'PendingApproval';
